/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { Search } from 'iconoir-react'
import { debounce } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'

import { cn } from 'app/lib/utils'
import { Input } from './input'

export function ControlledSearch({
  onValueChange,
  placeholder = 'Search',
  className,
  value,
}: {
  onValueChange: (val: string) => void
  placeholder?: string
  className?: string
  /**
   * Current search text, for resetting the box from outside (for example the
   * empty state's Clear button). The input stays uncontrolled (the 250ms
   * debounce needs it to be): `value` is applied as a `defaultValue` via a
   * remount key, not as a controlled `value` on the underlying input.
   *
   * Contract for callers that round-trip this prop from `onValueChange`
   * (store the callback's argument in state and pass it straight back in as
   * `value`): that round trip is safe and will NOT remount or steal focus
   * mid-typing. ControlledSearch remembers the last value it reported
   * through `onValueChange` and only remounts when the incoming `value`
   * differs from that, i.e. when it changed for a reason other than this
   * component's own keystrokes (a Clear action, or any other external
   * change). Do not derive `value` from anything other than the debounced
   * `onValueChange` text itself (e.g. don't trim or transform it before
   * passing it back), or a legitimate echo will be misread as external.
   */
  value?: string
}) {
  // Bumped only when an external `value` change (one this component did not
  // just report itself) is detected, to key a remount of the uncontrolled
  // input below. Kept separate from `value`'s own text: if the key were
  // `value` itself, resetting back to a value that was already rendered once
  // (e.g. clearing back to `""` right after mount) would leave the key
  // unchanged, React would bail out of the `setState`, and the remount would
  // silently no-op, leaving stale typed text in the DOM.
  const [resetCount, setResetCount] = useState(0)
  // Last value this component itself reported through onValueChange, so the
  // effect below can recognize a caller round-tripping that same value back
  // in as `value` (see the doc above) and skip the remount, instead of
  // treating every debounced keystroke as an external reset.
  const lastReported = useRef(value)

  useEffect(() => {
    if (value !== undefined && value !== lastReported.current) {
      lastReported.current = value
      setResetCount((count) => count + 1)
    }
  }, [value])

  const handleChange = useMemo(
    () =>
      debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        lastReported.current = e.target.value
        onValueChange(e.target.value)
      }, 250),
    [onValueChange],
  )

  return (
    <Input
      key={resetCount}
      wrapperClassName={cn('ml-0.5 h-8 w-62.5', className)}
      className="h-full transition-colors hover:bg-surface-muted"
      icon={<Search className="size-4 text-minimal" />}
      defaultValue={value}
      type="search"
      name="search"
      placeholder={placeholder}
      onChange={handleChange}
      autoComplete="off"
    />
  )
}
